import React from "react";
import { Link } from "react-router-dom";
import ProntoLogo from "@/components/ProntoLogo";
import LandingFooter from "@/pages/landing/LandingFooter";
import { CHROME_STORE_URL } from "@/lib/extensionConstants";
import {
  ArrowRight, Lightning, CheckCircle, Sparkle, Clock,
  ShieldCheck, Browser, Lock, Cursor,
} from "@phosphor-icons/react";

/**
 * Estensione · iter 305 round 12
 *
 * Public landing page (no auth) for Pronto Publisher Chrome extension.
 * Designed as acquisition asset for:
 *   - LinkedIn posts after extension goes live on Chrome Web Store
 *   - Facebook real estate agent communities
 *   - Pronto Marketing pages linking
 *   - Word-of-mouth referrals from existing users
 *
 * Hard structure:
 *   1. Hero · Headline onesto + CTA Aggiungi a Chrome
 *   2. Portali coperti (4 logos)
 *   3. Come funziona (3 step)
 *   4. Cosa fa risparmiare (per agente con 1-3/5-15/20+ annunci)
 *   5. Privacy reassurance (importante: italiani diffidenti su extension)
 *   6. FAQ minimal
 *   7. CTA finale + footer
 *
 * IMPORTANT: until extension passes Google review, the CTAs link to a
 * waitlist email-capture (modal o pre-iscrizione). We'll switch them to
 * direct Web Store URL when live.
 *
 * iter 305 r13: `CHROME_STORE_URL` ora vive in `/lib/extensionConstants.js`
 * come single source of truth — quando Google approva basta cambiare lì
 * (e settare `EXTENSION_LIVE = true`).
 */

const PORTALS = [
  { name: "Immobiliare.it", color: "#0033A0", note: "Prezzo · Descrizione · Contatti automatici. Altri campi con bottone Copia." },
  { name: "Idealista", color: "#FF6E00", note: "Compilazione quasi totale (~95%) dei campi del wizard." },
];

export default function Estensione() {
  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#1C1C1A]" data-testid="estensione-page">
      {/* Header */}
      <header className="border-b border-[#EAE7DF] bg-white sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="estensione-home-link">
            <ProntoLogo />
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/auth"
              className="hidden sm:inline-flex items-center gap-1 text-[13px] text-[#6B6A65] hover:text-[#1C1C1A] transition-colors"
              data-testid="estensione-login-link"
            >
              Accedi
            </Link>
            <a
              href={CHROME_STORE_URL}
              className="inline-flex items-center gap-2 bg-[#1C1C1A] hover:bg-black text-white font-semibold text-[13px] px-4 py-2 rounded-md transition-colors"
              data-testid="estensione-header-cta"
            >
              <Lightning size={14} weight="fill" />
              Aggiungi a Chrome
            </a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="px-6 pt-16 sm:pt-20 pb-12 sm:pb-16" data-testid="estensione-hero">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E] mb-4">
              Estensione Chrome · gratuita · per agenti immobiliari
            </div>
            <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6">
              Aiuta a compilare gli annunci su <br />
              <span className="text-[#7C8B6E]">Idealista</span> e <span className="text-[#7C8B6E]">Immobiliare</span>.
            </h1>
            <p className="text-base sm:text-lg text-[#1C1C1A] leading-relaxed max-w-xl mb-3">
              Pronto Publisher è l'estensione Chrome che ti riempie i campi
              del wizard di pubblicazione con i dati dell'annuncio che hai già in Pronto.
            </p>
            <p className="text-[14px] text-[#6B6A65] leading-relaxed max-w-xl mb-8">
              Su <strong>Idealista</strong> riempie quasi tutto in automatico (~95%).
              Su <strong>Immobiliare.it</strong> compila prezzo, descrizione e contatti
              in automatico; per gli altri campi (località, caratteristiche, menu a tendina, foto)
              ti fornisce i valori pronti con il bottone <strong>Copia</strong>, perché il portale
              blocca la compilazione esterna.
            </p>

            {/* CTA primary */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center mb-6">
              <a
                href={CHROME_STORE_URL}
                className="inline-flex items-center gap-2 bg-[#1C1C1A] hover:bg-black text-white font-semibold text-[15px] px-6 py-3.5 rounded-md transition-colors shadow-sm"
                data-testid="estensione-hero-cta"
              >
                <Lightning size={16} weight="fill" />
                Aggiungi a Chrome · gratuita
                <ArrowRight size={16} />
              </a>
              <Link
                to="/auth"
                className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#1C1C1A] hover:text-[#7C8B6E] transition-colors"
                data-testid="estensione-hero-secondary"
              >
                Crea account Pronto gratuito →
              </Link>
            </div>

            <div className="flex items-center gap-5 text-[11.5px] text-[#9A968B] flex-wrap">
              <span className="inline-flex items-center gap-1.5"><CheckCircle size={13} weight="fill" className="text-[#7C8B6E]" /> Senza carta di credito</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle size={13} weight="fill" className="text-[#7C8B6E]" /> Il click "Pubblica" resta tuo</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle size={13} weight="fill" className="text-[#7C8B6E]" /> Niente CAPTCHA bypass</span>
            </div>
          </div>

          {/* Right · visual mockup of the floating panel */}
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl bg-gradient-to-br from-[#F1F4ED] via-white to-[#F1F4ED] p-8 sm:p-10 shadow-sm border border-[#EAE7DF]">
              {/* Browser chrome decoration */}
              <div className="flex items-center gap-1.5 mb-5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF6B6B]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#FFCC4D]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#79DC79]" />
                <span className="ml-3 text-[10.5px] text-[#9A968B] font-mono truncate">immobiliare.it/.../caratteristiche/</span>
              </div>

              {/* Mock panel */}
              <div className="bg-white rounded-xl shadow-lg border border-[#E5E7E0] overflow-hidden" data-testid="estensione-hero-mockup">
                <div className="bg-[#7C8B6E] text-white px-4 py-3">
                  <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-[#A8B89A] mb-0.5">
                    PUBBLICAZIONE GUIDATA
                  </div>
                  <div className="font-editorial text-base">Immobiliare.it</div>
                  <div className="mt-2 h-1 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full w-1/2 bg-[#A8B89A]" />
                  </div>
                  <div className="text-[10.5px] text-white/70 mt-1.5">Step 3 di 8 · Caratteristiche</div>
                </div>
                <div className="p-3 space-y-1.5">
                  {[
                    ["Prezzo", "€ 285.000", true],
                    ["Descrizione", "Trilocale luminoso…", true],
                    ["Telefono agente", "+39 035…", true],
                    ["Superficie m²", "95", false],
                    ["Piano", "Secondo", false],
                    ["Classe energetica", "C", false],
                  ].map(([label, value, done], i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-2.5 px-2 py-1.5 rounded-md text-[11.5px] ${
                        done ? "bg-[#F1F4ED]" : "bg-white border border-[#E5D6A8]"
                      }`}
                    >
                      <span className={`flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                        done ? "bg-[#7C8B6E] text-white" : "bg-[#F0CC78] text-[#5A4017]"
                      }`}>
                        {done ? "✓" : "!"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold ${done ? "text-[#7C8B6E]" : "text-[#1C1C1A]"}`}>{label}</div>
                        <div className={`truncate ${done ? "text-[#9A968B]" : "text-[#6B6A65]"}`}>{value}</div>
                      </div>
                      {!done && (
                        <span className="text-[10px] font-bold bg-[#7C8B6E] text-white px-2 py-1 rounded">Copia</span>
                      )}
                    </div>
                  ))}
                  <div className="text-[10px] text-[#9A968B] italic pt-1 px-1">
                    Auto: Prezzo · Descrizione · Contatti.<br />Copia &amp; Incolla: Località · Caratteristiche · Menu.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PORTALI COPERTI */}
      <section className="px-6 py-10 sm:py-14 bg-white border-y border-[#EAE7DF]" data-testid="estensione-portali">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#7C8B6E] mb-2">
              Funziona sui 2 portali più usati in Italia
            </div>
            <p className="text-[15px] text-[#6B6A65]">
              Immobiliare.it e Idealista coprono <strong className="text-[#1C1C1A]">oltre l'85% del mercato</strong> annunci immobiliari Italia.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-5 max-w-xl mx-auto">
            {PORTALS.map((p) => (
              <div
                key={p.name}
                className="bg-[#FAFAF7] border border-[#EAE7DF] rounded-xl px-4 py-5 flex items-start gap-3"
                data-testid={`estensione-portal-${p.name.toLowerCase().replace(/\./g, "")}`}
              >
                <span className="w-1.5 h-10 rounded-full shrink-0 mt-0.5" style={{ background: p.color }} />
                <div className="min-w-0">
                  <div className="font-semibold text-[14px] text-[#1C1C1A]">{p.name}</div>
                  <div className="text-[11.5px] text-[#6B6A65] mt-0.5 leading-snug">
                    {p.note}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COME FUNZIONA · 3 step */}
      <section className="px-6 py-14 sm:py-20" data-testid="estensione-come-funziona">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="overline mb-2">Come funziona</div>
            <h2 className="font-editorial text-3xl sm:text-4xl tracking-tight">
              Tre passaggi per pubblicare.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                num: "1",
                Icon: Sparkle,
                title: "Crei l'annuncio su Pronto",
                body: "Inserisci dati e foto. Pronto migliora le foto, prepara una descrizione su misura per ogni portale e organizza tutto.",
              },
              {
                num: "2",
                Icon: Browser,
                title: "Apri il portale, accedi col tuo account",
                body: "Su Immobiliare.it o Idealista compare il pannello Pronto. Su Idealista compila quasi tutto in automatico. Su Immobiliare compila prezzo, descrizione, contatti e ti dà i valori degli altri campi pronti da copiare (Comune, Via, m², piano, classe energetica…).",
              },
              {
                num: "3",
                Icon: CheckCircle,
                title: "Tu carichi foto e premi Pubblica",
                body: "Foto e planimetria le carichi tu con drag-and-drop (i portali usano uploader proprietari). Il click finale \"Pubblica\" resta sempre tuo: Pronto è un assistente alla compilazione, non un sistema di pubblicazione automatica.",
              },
            ].map(({ num, Icon, title, body }) => (
              <div
                key={num}
                className="bg-white border border-[#EAE7DF] rounded-xl p-6 sm:p-7"
                data-testid={`estensione-step-${num}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#7C8B6E] text-white flex items-center justify-center font-editorial text-lg shrink-0">
                    {num}
                  </div>
                  <Icon size={18} weight="duotone" className="text-[#7C8B6E] mt-2.5" />
                </div>
                <h3 className="font-editorial text-xl mb-2 leading-tight">{title}</h3>
                <p className="text-[13.5px] text-[#6B6A65] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a
              href={CHROME_STORE_URL}
              className="inline-flex items-center gap-2 bg-[#1C1C1A] hover:bg-black text-white font-semibold text-[14.5px] px-6 py-3 rounded-md transition-colors"
              data-testid="estensione-come-funziona-cta"
            >
              <Lightning size={15} weight="fill" />
              Provala adesso · gratuita
              <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* PER CHI CONVIENE */}
      <section className="px-6 py-14 sm:py-20 bg-white border-y border-[#EAE7DF]" data-testid="estensione-per-chi">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <div className="overline mb-2">Onesto · per chi conviene</div>
            <h2 className="font-editorial text-3xl sm:text-4xl tracking-tight mb-4">
              Funziona se hai 1 annuncio o 100. Non importa.
            </h2>
            <p className="text-[14.5px] text-[#6B6A65] leading-relaxed">
              La maggior parte degli automatismi sui portali serve solo a chi pubblica 50+ annunci/mese.
              Pronto Publisher cambia il calcolo: <strong className="text-[#1C1C1A]">a parità di numero, ti fa risparmiare il 90% del tempo.</strong>
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { range: "1-3", title: "annunci al mese", saving: "2 ore", color: "#7C8B6E", body: "Pronto ti risparmia il copywriting + la post-produzione foto + i 2 testi diversi per i 2 portali. Da 30 min a 3 min per ogni annuncio. Diventa un \"perché no?\"" },
              { range: "5-15", title: "annunci al mese", saving: "8 ore", color: "#C49A2A", body: "Qui Pronto Publisher diventa indispensabile. Risparmi quasi una giornata di lavoro al mese, che usi per andare a vedere case nuove o gestire trattative." },
              { range: "20+", title: "annunci al mese", saving: "20+ ore", color: "#7C8B6E", body: "Sei un'agenzia. Pronto Publisher diventa parte del workflow. Combinato col Feed XML automatico (se hai Pro), la pubblicazione è quasi azzerata." },
            ].map(({ range, title, saving, color, body }) => (
              <div
                key={range}
                className="border border-[#EAE7DF] rounded-xl p-6 sm:p-7 bg-[#FAFAF7]"
                data-testid={`estensione-segment-${range}`}
              >
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-editorial text-4xl tracking-tight" style={{ color }}>{range}</span>
                  <span className="text-[12.5px] text-[#6B6A65] font-semibold">{title}</span>
                </div>
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#9A968B] mt-3 mb-2 font-semibold">
                  Risparmio stimato/mese
                </div>
                <div className="font-editorial text-2xl tracking-tight mb-3" style={{ color }}>{saving}</div>
                <p className="text-[12.5px] text-[#6B6A65] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRIVACY · trust block */}
      <section className="px-6 py-14 sm:py-20" data-testid="estensione-privacy">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <Lock size={28} weight="duotone" className="text-[#7C8B6E] mx-auto mb-3" />
            <div className="overline mb-2">Privacy onesta</div>
            <h2 className="font-editorial text-3xl sm:text-4xl tracking-tight mb-4">
              Non leggiamo niente di quello che fai online.
            </h2>
          </div>
          <div className="space-y-4 text-[14px] text-[#1C1C1A] leading-relaxed">
            <div className="flex items-start gap-3">
              <CheckCircle size={18} weight="fill" className="text-[#7C8B6E] mt-0.5 shrink-0" />
              <p>
                L'estensione si attiva <strong>solo</strong> sulle pagine di inserimento annunci
                di Immobiliare.it e Idealista. Su Gmail, Facebook, banca o qualsiasi altro sito è
                completamente inattiva.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle size={18} weight="fill" className="text-[#7C8B6E] mt-0.5 shrink-0" />
              <p>
                I dati dell'annuncio passano <strong>direttamente</strong> dal tuo account
                Pronto al portale. Non transitano mai sui nostri server.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle size={18} weight="fill" className="text-[#7C8B6E] mt-0.5 shrink-0" />
              <p>
                Lo storage locale del browser viene cancellato dopo <strong>3 ore</strong> di inattività.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle size={18} weight="fill" className="text-[#7C8B6E] mt-0.5 shrink-0" />
              <p>Niente analytics. Niente cookie pubblicitari. Niente condivisione con terzi.</p>
            </div>
          </div>
          <div className="mt-8 text-center">
            <Link
              to="/privacy-extension"
              className="text-[13px] text-[#7C8B6E] hover:text-[#7C8B6E] underline underline-offset-4"
              data-testid="estensione-privacy-link"
            >
              Leggi l'informativa completa →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ · minimal */}
      <section className="px-6 py-14 sm:py-20 bg-white border-y border-[#EAE7DF]" data-testid="estensione-faq">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="overline mb-2">Domande frequenti</div>
            <h2 className="font-editorial text-3xl sm:text-4xl tracking-tight">
              Le cose che ti stai chiedendo.
            </h2>
          </div>

          <div className="space-y-3">
            {[
              {
                q: "Costa qualcosa l'estensione?",
                a: "L&apos;estensione è gratuita. Per usarla ti serve un account Pronto (anche il piano gratuito va bene per provare).",
              },
              {
                q: "Funziona su tutti i computer?",
                a: "Funziona su Google Chrome (Windows, Mac, Linux). Stiamo lavorando per Edge e Firefox. Su Safari serve un altro processo perché Apple chiede un account sviluppatore diverso.",
              },
              {
                q: "Se il portale cambia design, l'estensione si rompe?",
                a: "Sì, è il rischio fisiologico di qualsiasi estensione di questo tipo. Quando succede, lo rileviamo entro ore (abbiamo monitoraggio automatico) e rilasciamo una versione aggiornata. Tu non devi fare nulla, l'estensione si auto-aggiorna.",
              },
              {
                q: "Tutti i campi si compilano sempre?",
                a: "No, dipende dal portale. Su Idealista compila quasi tutto (~95%). Su Immobiliare.it il portale blocca la compilazione dei menu a tendina, dell'autocomplete della località e dei dropdown delle caratteristiche: Pronto compila in automatico Prezzo, Descrizione, Contatti, e ti dà tutti gli altri valori (Comune, Via, m², piano, classe energetica, stato, esposizione, riscaldamento…) con un bottone Copia, così fai copia-incolla senza dover passare da Pronto ogni volta. Foto e planimetria le trascini tu nel portale (i due uploader sono proprietari).",
              },
              {
                q: "I miei dati restano sicuri?",
                a: "Sì. L'estensione attinge i dati direttamente dal tuo account Pronto (protetto da password e crittografia HTTPS). Non passa mai per server esterni. Vedi sopra la sezione Privacy.",
              },
              {
                q: "Devo essere un'agenzia per usarla?",
                a: "No. Funziona anche per agenti immobiliari indipendenti e anche per privati con account Pronto. L'estensione legge il tuo account e basta.",
              },
            ].map(({ q, a }, i) => (
              <details
                key={i}
                className="bg-[#FAFAF7] border border-[#EAE7DF] rounded-lg group"
                data-testid={`estensione-faq-${i}`}
              >
                <summary className="cursor-pointer p-5 font-semibold text-[14.5px] text-[#1C1C1A] flex items-center justify-between gap-3 list-none">
                  <span>{q}</span>
                  <span className="text-[#9A968B] group-open:rotate-180 transition-transform shrink-0">▾</span>
                </summary>
                <div className="px-5 pb-5 text-[13.5px] text-[#6B6A65] leading-relaxed">{a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="px-6 py-16 sm:py-24 bg-gradient-to-br from-[#7C8B6E] to-[#7C8B6E] text-white" data-testid="estensione-cta-final">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#A8B89A] mb-4">
            Ultima cosa
          </div>
          <h2 className="font-editorial text-3xl sm:text-5xl tracking-tight leading-[1.1] mb-5">
            Provala e basta.
            <br />
            <span className="italic-accent text-[#A8B89A]">È gratuita e l'install dura un minuto.</span>
          </h2>
          <p className="text-[15px] text-white/80 leading-relaxed mb-8 max-w-xl mx-auto">
            Se non ti aiuta, disinstalli. Niente carta di credito, niente vincoli.
          </p>
          <a
            href={CHROME_STORE_URL}
            className="inline-flex items-center gap-2.5 bg-white hover:bg-[#F1F4ED] text-[#1C1C1A] font-semibold text-[15px] px-7 py-3.5 rounded-md transition-colors shadow-lg"
            data-testid="estensione-final-cta"
          >
            <Lightning size={16} weight="fill" />
            Aggiungi a Chrome · gratuita
            <ArrowRight size={15} />
          </a>
          <div className="mt-6 text-[12px] text-white/60">
            Nessuna carta di credito · disinstalli quando vuoi
          </div>
        </div>
      </section>

      {/* Footer condiviso */}
      <LandingFooter />
    </div>
  );
}
